/**
 * Routing / auth-gating (feature #1 & #10).
 *
 * ForceUpdateGate wraps everything: if the running APP_VERSION is below
 * app_config.minAppVersion, it blocks the whole app with an update screen.
 *
 * ApprovedRoute protects the Home/product/profile area: only an authenticated
 * user whose users/{uid}.status === 'approved' may enter (mirrors the Firestore
 * rule that only approved dealers can read products).
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { useAppConfig } from '../hooks/useAppConfig';
import { FullScreenSpinner } from '../components/Spinner';
import { APP_VERSION, isBelowMinVersion } from '../version';
import { ForceUpdateScreen } from '../features/auth/ForceUpdateScreen';
import { ROUTES } from './paths';

export function ForceUpdateGate({ children }: { children: ReactNode }): JSX.Element {
  const config = useAppConfig();
  if (config && config.minAppVersion && isBelowMinVersion(APP_VERSION, config.minAppVersion)) {
    return <ForceUpdateScreen minVersion={config.minAppVersion} />;
  }
  return <>{children}</>;
}

/** Guards the approved-only area. Redirects based on auth phase/status. */
export function ApprovedRoute({ children }: { children: ReactNode }): JSX.Element {
  const { phase, status } = useAuth();
  const config = useAppConfig();
  // Absent config/field = approval required (mirror of the Firestore rule).
  const approvalRequired = config?.approvalRequired ?? true;

  if (phase === 'loading') return <FullScreenSpinner label="Loading…" />;
  if (phase === 'signedOut') return <Navigate to={ROUTES.login} replace />;
  // Signed in via OTP but no profile doc yet → must register.
  if (phase === 'needsRegistration') return <Navigate to={ROUTES.register} replace />;

  // signed in with a profile — route by status
  switch (status) {
    case 'approved':
      return <>{children}</>;
    case 'pending':
    case 'unknown':
      // Approval toggle OFF → pending users browse like anyone else.
      if (!approvalRequired) return <>{children}</>;
      return <Navigate to={ROUTES.underReview} replace />;
    case 'blocked':
    case 'rejected':
      return <Navigate to={ROUTES.blocked} replace />;
    default:
      return <Navigate to={ROUTES.login} replace />;
  }
}

/**
 * Guards the post-OTP Registration screen: only a signed-in user WITHOUT a
 * profile doc belongs here. Signed-out users go to Login; users who already
 * have a doc are routed by the Splash screen based on status.
 */
export function RegistrationRoute({ children }: { children: ReactNode }): JSX.Element {
  const { phase } = useAuth();

  if (phase === 'loading') return <FullScreenSpinner label="Loading…" />;
  if (phase === 'signedOut') return <Navigate to={ROUTES.login} replace />;
  if (phase === 'needsRegistration') return <>{children}</>;
  // Already has a profile doc → let the Splash screen route by status.
  return <Navigate to={ROUTES.splash} replace />;
}
